<template>
  <div class="ovg-page">
    <div class="ovg-page__bg" aria-hidden="true" />

    <header class="ovg-header">
      <div
        class="ovg-header__logos"
        @click="$router.push({ name: 'ovg-landing' })"
      >
        <img
          src="@/assets/logo_original.svg"
          alt="Samtek"
          class="ovg-header__logo-samtek"
        />
        <img
          src="@/assets/logo_asus.png"
          alt="ASUS"
          class="ovg-header__logo-asus"
        />
      </div>
      <button
        class="ovg-header__salir"
        @click="$router.push({ name: 'ovg-landing' })"
      >
        Salir <font-awesome-icon icon="sign-out-alt" />
      </button>
    </header>

    <div class="ovg-subheader">
      <h2 class="ovg-subheader__title">Confirma y realiza tu pago</h2>
      <p class="ovg-subheader__subtitle">
        Revisa los detalles de tu garantía y procede al pago seguro con Getnet.
      </p>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="ovg-card ovg-card--centrado">
      <span class="ovg-spinner" />
      <p class="ovg-loading-text">Cargando información de pago...</p>
    </div>

    <!-- Ya pagada (retorno tras pago exitoso / estado comprado) -->
    <div v-else-if="yaPagada" class="ovg-card ovg-card--centrado">
      <font-awesome-icon
        icon="check-circle"
        class="ovg-estado__icon ovg-estado__icon--success"
      />
      <h3 class="ovg-estado__titulo">¡Pago recibido!</h3>
      <p class="ovg-estado__texto">
        Tu garantía extendida ya está activa. Recibirás la confirmación y los
        documentos en tu correo electrónico.
      </p>
      <button
        type="button"
        class="ovg-btn ovg-btn--primary"
        @click="$router.push({ name: 'ovg-landing' })"
      >
        Volver al inicio
      </button>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="ovg-card ovg-card--centrado">
      <font-awesome-icon
        icon="exclamation-triangle"
        class="ovg-estado__icon ovg-estado__icon--warn"
      />
      <h3 class="ovg-estado__titulo">No se pudo cargar la información</h3>
      <p class="ovg-estado__texto">
        El enlace de pago no es válido o ha expirado. Por favor intenta el
        proceso nuevamente.
      </p>
      <button
        type="button"
        class="ovg-btn ovg-btn--primary"
        @click="$router.push({ name: 'ovg-landing' })"
      >
        Volver al inicio
      </button>
    </div>

    <!-- Contenido de pago -->
    <div v-else-if="pagoData" class="ovg-card">
      <h3 class="ovg-section-title">Resumen de tu pedido</h3>

      <!-- Productos (extensión + protecciones) -->
      <div v-for="prod in productos" :key="prod.id" class="ovg-producto-row">
        <svg
          class="ovg-producto-row__icon"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2L3 7v5c0 5.25 3.83 10.16 9 11.35C17.17 22.16 21 17.25 21 12V7L12 2z"
            fill="#22c55e"
            fill-opacity="0.15"
          />
          <path
            d="M12 2L3 7v5c0 5.25 3.83 10.16 9 11.35C17.17 22.16 21 17.25 21 12V7L12 2z"
            stroke="#22c55e"
            stroke-width="2"
            stroke-linejoin="round"
          />
          <path
            d="M9 12l2 2 4-4"
            stroke="#22c55e"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <div class="ovg-producto-row__info">
          <span class="ovg-producto-row__nombre">{{ prod.nombre }}</span>
          <span v-if="prod.vigencia_meses" class="ovg-producto-row__detalle">
            {{ prod.vigencia_meses }} meses de cobertura
          </span>
        </div>
        <span class="ovg-producto-row__precio">{{
          formatPrecio(prod.precio)
        }}</span>
      </div>

      <!-- Datos del equipo -->
      <div class="ovg-info-box">
        <div v-if="pagoData.modelo" class="ovg-info-box__row">
          <span class="ovg-info-box__label">Modelo</span>
          <span class="ovg-info-box__value">{{ pagoData.modelo }}</span>
        </div>
        <div v-if="pagoData.numero_serie" class="ovg-info-box__row">
          <span class="ovg-info-box__label">N° de serie</span>
          <span class="ovg-info-box__value">{{ pagoData.numero_serie }}</span>
        </div>
      </div>

      <!-- Separador -->
      <hr class="ovg-divider" />

      <!-- Sección facturación [SILL-1415] -->
      <div class="ovg-factura-section">
        <div class="ovg-factura-section__textos">
          <h3 class="ovg-factura-section__title">Facturación</h3>
          <p class="ovg-factura-section__subtitle">
            Seleccione esta opción si necesitas Factura
          </p>
        </div>

        <label class="ovg-check">
          <input
            type="checkbox"
            class="ovg-check__native"
            :checked="requireFactura"
            @change="toggleFactura"
          />
          <span
            class="ovg-check__box"
            :class="{ 'ovg-check__box--active': requireFactura }"
          >
            <font-awesome-icon v-if="requireFactura" icon="check" />
          </span>
          <span class="ovg-check__label">Factura</span>
        </label>

        <div v-if="requireFactura" class="ovg-factura-form">
          <div class="ovg-rut-row">
            <div class="ovg-field ovg-field--320">
              <label class="ovg-label">Rut</label>
              <input
                v-model="factura.rut"
                type="text"
                placeholder="Ej: 96.876.543-2"
                class="ovg-input"
                :class="{ 'ovg-input--error': !!rutError }"
                :disabled="validandoRut"
                @input="onRutInput"
              />
            </div>
            <button
              type="button"
              class="ovg-btn ovg-btn--validar"
              :disabled="!factura.rut || validandoRut || facturaValidada"
              @click="validarRut"
            >
              {{ validandoRut ? 'Validando...' : 'Validar Rut' }}
            </button>
          </div>
          <span v-if="rutError" class="ovg-error">{{ rutError }}</span>

          <template v-if="facturaValidada">
            <div class="ovg-field ovg-field--320">
              <label class="ovg-label">Razón Social</label>
              <input
                v-model="factura.razon_social"
                type="text"
                class="ovg-input"
                readonly
              />
            </div>
            <div class="ovg-field ovg-field--320">
              <label class="ovg-label">Giro</label>
              <input
                v-model="factura.giro"
                type="text"
                class="ovg-input"
                readonly
              />
            </div>
          </template>
        </div>
      </div>

      <!-- Total -->
      <div class="ovg-total-row">
        <span class="ovg-total-row__label">Total a pagar</span>
        <span class="ovg-total-row__precio">{{ formatPrecio(totalPago) }}</span>
      </div>

      <div class="ovg-card__footer">
        <button
          type="button"
          class="ovg-btn ovg-btn--primary ovg-btn--pagar"
          :disabled="pagando || (requireFactura && !facturaValidada)"
          @click="irAPagar"
        >
          {{ pagando ? 'Redirigiendo...' : 'Pagar con Getnet' }}
        </button>
      </div>
    </div>

    <!-- Botón de contacto WhatsApp: oculto hasta que el negocio lo habilite
    <a
      href="https://wa.me"
      class="ovg-contact-btn"
      target="_blank"
      rel="noopener"
    >
      <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22">
        <path :d="waSvgPath" />
      </svg>
    </a>
    -->

    <p class="ovg-page__footer">
      © 2026 Samtek | Servicio Técnico ASUS Autorizado en Chile.
    </p>
  </div>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'PagoView',
  data() {
    return {
      waSvgPath:
        // eslint-disable-next-line max-len
        'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z',
      loading: false,
      error: false,
      yaPagada: false,
      pagoData: null,
      requireFactura: false,
      validandoRut: false,
      facturaValidada: false,
      rutError: '',
      pagando: false,
      factura: {
        rut: '',
        razon_social: '',
        giro: '',
      },
    }
  },
  computed: {
    // Productos del pedido: usa `productos` del backend (extensión +
    // protecciones); cae a `garantia` por compatibilidad.
    productos() {
      if (!this.pagoData) return []
      if (Array.isArray(this.pagoData.productos)) return this.pagoData.productos
      return this.pagoData.garantia ? [this.pagoData.garantia] : []
    },
    totalPago() {
      if (this.pagoData && this.pagoData.precio_total != null) {
        return this.pagoData.precio_total
      }
      return this.productos.reduce(
        (acc, p) => acc + parseFloat(p.precio || 0),
        0
      )
    },
  },
  created() {
    this.cargarDatosPago()
  },
  methods: {
    ...mapActions('OVGStore', [
      'fetchDatosPago',
      'validarRutContribuyente',
      'guardarFacturaPago',
      'iniciarPagoOvg',
    ]),
    cargarDatosPago() {
      const { token } = this.$route.params
      if (!token) {
        this.error = true
        return
      }
      this.loading = true
      this.error = false
      this.yaPagada = false
      this.fetchDatosPago(token)
        .then((data) => {
          this.pagoData = data
        })
        .catch((err) => {
          // 409: la garantía ya fue pagada (estado 'comprado') — es el retorno
          // tras un pago exitoso, no un error. 404/otros: link inválido/expirado.
          if (err && err.response && err.response.status === 409) {
            this.yaPagada = true
          } else {
            this.error = true
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    toggleFactura() {
      this.requireFactura = !this.requireFactura
      if (!this.requireFactura) {
        this.facturaValidada = false
        this.rutError = ''
      }
    },
    onRutInput() {
      // Cambiar el RUT obliga a re-validarlo.
      this.facturaValidada = false
      this.rutError = ''
    },
    validarRut() {
      const rut = (this.factura.rut || '').trim()
      if (!rut) return
      this.validandoRut = true
      this.rutError = ''
      // Endpoint público de OVG06 (verificado: api/oportunidadventa/...).
      this.validarRutContribuyente(rut)
        .then((data) => {
          this.factura.razon_social = (data && data.razon_social) || ''
          this.factura.giro = (data && data.giro) || ''
          this.facturaValidada = true
        })
        .catch((err) => {
          this.facturaValidada = false
          if (err && err.response && err.response.status === 400) {
            this.rutError =
              (err.response.data && err.response.data.error) ||
              'RUT no encontrado en OpenFactura.'
          } else {
            this.rutError = 'No se pudo validar el RUT. Intenta nuevamente.'
          }
        })
        .finally(() => {
          this.validandoRut = false
        })
    },
    irAPagar() {
      if (!this.pagoData) return
      if (this.requireFactura && !this.facturaValidada) {
        this.rutError = 'Valida el RUT antes de continuar.'
        return
      }
      this.pagando = true
      const { token } = this.$route.params

      const guardarFactura = this.requireFactura
        ? this.guardarFacturaPago({
            token,
            rut_contribuyente: this.factura.rut,
            razon_social_contribuyente: this.factura.razon_social,
          })
        : Promise.resolve()

      guardarFactura
        // La sesión de Getnet dura ~15 min; la que se creó al aprobar la OVG
        // ya expiró para cuando el cliente llega aquí, así que se crea una
        // nueva justo antes de redirigir a pagar.
        .then(() => this.iniciarPagoOvg(token))
        .then((data) => {
          window.location.href = data.getnet_checkout_url
        })
        .catch(() => {
          this.pagando = false
          this.rutError = this.requireFactura
            ? 'No se pudo registrar la factura. Intenta nuevamente.'
            : 'No se pudo iniciar el pago. Intenta nuevamente.'
        })
    },
    formatPrecio(precio) {
      const num = parseFloat(precio)
      if (Number.isNaN(num)) return precio
      return new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency: 'CLP',
      }).format(num)
    },
  },
}
</script>

<style scoped>
.ovg-page {
  font-family: 'Noto Sans', sans-serif;
  min-height: 100vh;
  background-color: #f5f6fa;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding-bottom: 80px;
}

.ovg-page__bg {
  /* Sin `opacity`: el PNG ya es una marca de agua (su patrón viene con
     alpha 13/255). Desvanecerlo otra vez acá lo dejaba en 0,62% de
     contraste, o sea invisible en pantalla. */
  position: fixed;
  inset: 0;
  background-image: url('~@/assets/fondo_samtek.png');
  background-size: cover;
  background-position: center;
  pointer-events: none;
  z-index: 0;
}

.ovg-header {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 55px;
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 162px;
  flex-shrink: 0;
}

.ovg-header__logos {
  display: flex;
  align-items: center;
  gap: 15px;
  cursor: pointer;
}

.ovg-header__logo-samtek {
  height: 18px;
  width: auto;
}

.ovg-header__logo-asus {
  height: 18px;
  width: auto;
}

.ovg-header__salir {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  color: #23201c;
  font-family: 'Noto Sans', sans-serif;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
}

.ovg-subheader {
  position: relative;
  z-index: 1;
  width: 100%;
  background: #0a192f;
  min-height: 195px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 24px 20px;
  text-align: center;
}

.ovg-subheader__title {
  font-size: 28px;
  font-weight: 700;
  color: #f5f6fa;
  margin: 0;
}

.ovg-subheader__subtitle {
  font-size: 15px;
  font-weight: 400;
  color: #f5f6fa;
  margin: 0;
  line-height: 1.5;
}

.ovg-card {
  position: relative;
  z-index: 1;
  background: #ffffff;
  border-radius: 8px;
  padding: 20px;
  width: 700px;
  max-width: calc(100% - 40px);
  box-shadow: 0px 8px 12px rgba(0, 0, 0, 0.25);
  margin-top: -20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.ovg-card--centrado {
  align-items: center;
  text-align: center;
}

.ovg-loading-text {
  font-size: 15px;
  color: #525252;
  margin: 0;
}

.ovg-spinner {
  display: inline-block;
  width: 28px;
  height: 28px;
  border: 3px solid #d2d2d2;
  border-top-color: #525252;
  border-radius: 50%;
  animation: ovg-spin 0.6s linear infinite;
  flex-shrink: 0;
}

@keyframes ovg-spin {
  to {
    transform: rotate(360deg);
  }
}

.ovg-section-title {
  font-size: 18px;
  font-weight: 700;
  color: #23201c;
  margin: 0;
}

.ovg-producto-row {
  display: flex;
  align-items: center;
  gap: 12px;
  border: 1px solid #444444;
  border-radius: 4px;
  height: 80px;
  padding: 10px;
}

.ovg-producto-row__icon {
  width: 40px;
  height: 40px;
  flex-shrink: 0;
}

.ovg-producto-row__info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
}

.ovg-producto-row__nombre {
  font-size: 15px;
  font-weight: 700;
  color: #23201c;
}

.ovg-producto-row__detalle {
  font-size: 13px;
  color: #525252;
}

.ovg-producto-row__precio {
  font-size: 18px;
  font-weight: 700;
  color: #203859;
  flex-shrink: 0;
}

.ovg-info-box {
  background: #dddde1;
  border: 1px solid #525252;
  border-radius: 5px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
}

.ovg-info-box--aviso {
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  background: #eff6ff;
  border-color: #1d4ed8;
}

.ovg-info-box__icon {
  color: #1d4ed8;
  flex-shrink: 0;
  margin-top: 2px;
}

.ovg-info-box__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
}

.ovg-info-box__label {
  color: #525252;
  font-weight: 400;
}

.ovg-info-box__value {
  color: #23201c;
  font-weight: 600;
  text-align: right;
}

.ovg-divider {
  border: none;
  border-top: 1px solid #d2d2d2;
  margin: 0;
}

.ovg-factura-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ovg-factura-section__textos {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.ovg-factura-section__title {
  font-size: 18px;
  font-weight: 700;
  color: #23201c;
  margin: 0;
}

.ovg-factura-section__subtitle {
  font-size: 13px;
  font-weight: 400;
  color: #525252;
  margin: 0;
}

.ovg-check {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
}

.ovg-check__native {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.ovg-check__box {
  width: 24px;
  height: 24px;
  border: 2px solid #525252;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: #ffffff;
  font-size: 13px;
  color: #23201c;
  transition: border-color 0.15s, background 0.15s;
}

.ovg-check__box--active {
  border-color: #fece2f;
  background: #fece2f;
}

.ovg-check__label {
  font-size: 15px;
  font-weight: 500;
  color: #23201c;
}

.ovg-factura-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ovg-rut-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}

.ovg-field--320 {
  width: 320px;
  max-width: 100%;
}

.ovg-field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ovg-label {
  font-size: 15px;
  font-weight: 700;
  color: #23201c;
  margin: 0;
}

.ovg-input {
  height: 45px;
  border: 1px solid #525252;
  border-radius: 5px;
  padding: 0 10px;
  font-family: 'Noto Sans', sans-serif;
  font-size: 15px;
  color: #23201c;
  background: #ffffff;
  width: 100%;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.ovg-input::placeholder {
  color: #525252;
  font-weight: 400;
}

.ovg-input:focus {
  border-color: #fece2f;
}

.ovg-input--error {
  border-color: #dc3545;
}

.ovg-error {
  font-size: 13px;
  color: #dc3545;
}

.ovg-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 8px;
  border-top: 1px solid #d2d2d2;
}

.ovg-total-row__label {
  font-size: 16px;
  font-weight: 700;
  color: #23201c;
}

.ovg-total-row__precio {
  font-size: 22px;
  font-weight: 700;
  color: #203859;
}

.ovg-card__footer {
  display: flex;
  justify-content: flex-end;
}

.ovg-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 45px;
  padding: 10px 20px;
  border-radius: 8px;
  font-family: 'Noto Sans', sans-serif;
  font-size: 15px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: opacity 0.15s;
  white-space: nowrap;
}

.ovg-btn:hover {
  opacity: 0.9;
}

.ovg-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.ovg-btn--primary {
  background: #fece2f;
  color: #23201c;
}

.ovg-btn--validar {
  background: #17db94;
  color: #23201c;
  flex-shrink: 0;
}

.ovg-btn--pagar {
  width: 100%;
  font-size: 17px;
  font-weight: 700;
}

.ovg-estado__icon {
  font-size: 74px;
  width: 74px;
  height: 74px;
}

.ovg-estado__icon--warn {
  color: #f59e0b;
}

.ovg-estado__icon--success {
  color: #22c55e;
}

.ovg-estado__titulo {
  font-size: 22px;
  font-weight: 700;
  color: #23201c;
  margin: 0;
}

.ovg-estado__texto {
  font-size: 15px;
  color: #525252;
  margin: 0;
  line-height: 1.5;
  max-width: 480px;
}

.ovg-contact-btn {
  position: fixed;
  bottom: 90px;
  right: 70px;
  z-index: 10;
  width: 45px;
  height: 45px;
  background: #fece2f;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #23201c;
  font-size: 20px;
  text-decoration: none;
}

.ovg-page__footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
  font-size: 15px;
  font-weight: 400;
  color: #23201c;
  text-align: center;
  margin: 0;
  padding: 12px 0;
  border-top: 1px solid #d2d2d2;
  background: #f5f6fa;
}

/* ============================================================
   MOBILE — max-width: 767px
   ============================================================ */
@media (max-width: 767px) {
  /* Page */
  .ovg-page {
    padding-bottom: 60px;
  }

  /* Header */
  .ovg-header {
    padding: 0 20px;
  }

  /* Sub-header */
  .ovg-subheader {
    min-height: auto;
    padding: 24px 20px;
  }

  .ovg-subheader__title {
    font-size: 22px;
  }

  .ovg-subheader__subtitle {
    font-size: 14px;
  }

  /* Card */
  .ovg-card {
    width: calc(100% - 32px);
    border-radius: 16px;
  }

  /* Factura: RUT row → columna */
  .ovg-rut-row {
    flex-direction: column;
    align-items: stretch;
  }

  .ovg-field--320 {
    width: 100%;
  }

  /* Botón validar RUT → ancho completo */
  .ovg-btn--validar {
    width: 100%;
  }

  /* Producto row */
  .ovg-producto-row {
    height: auto;
    min-height: 60px;
  }
}
</style>
